import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { notify, fmtError } from "@/lib/notify";
import { Shield, RefreshCw, Loader2, Search, ExternalLink, AlertTriangle } from "lucide-react";

const POLARITY_STYLE = {
  negative: "bg-red-500/15 text-red-400 border-red-500/30",
  neutral: "bg-zinc-500/15 text-zinc-400 border-zinc-500/30",
  positive: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
};

export function DefenseView() {
  const [summary, setSummary] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [filter, setFilter] = useState({ kind: "", country: "", q: "" });

  const load = async () => {
    setLoading(true);
    try {
      const params = { limit: 100 };
      if (filter.kind) params.kind = filter.kind;
      if (filter.country) params.country = filter.country;
      if (filter.q) params.q = filter.q;
      const [s, e] = await Promise.all([
        api.get("/workspace/defense/summary"),
        api.get("/workspace/defense/events", { params }),
      ]);
      setSummary(s.data);
      setItems(e.data?.items || []);
    } catch (err) {
      notify.error(fmtError(err, "Chargement défense impossible."));
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter.kind, filter.country]);

  const refresh = async () => {
    setRefreshing(true);
    try {
      const r = await api.post("/workspace/defense/refresh");
      notify.success(`Scan terminé — ${r.data?.inserted ?? 0} nouveaux événements.`);
      await load();
    } catch (err) {
      notify.error(fmtError(err, "Scan impossible."));
    } finally { setRefreshing(false); }
  };

  const kinds = summary?.kinds || {};

  return (
    <div className="space-y-4" data-testid="defense-view">
      {summary && !summary.configured && (
        <div className="noam-card p-4 flex items-start gap-2 border-[#EF9F27]/30 bg-[#EF9F27]/5">
          <AlertTriangle className="w-4 h-4 text-[#EF9F27] flex-shrink-0 mt-0.5" />
          <div className="text-xs text-zinc-300">
            <strong className="text-[#EF9F27]">Scanner inactif.</strong> Clé <code className="px-1 bg-white/10 rounded">APITUBE_API_KEY</code> manquante dans le backend.
          </div>
        </div>
      )}

      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          <button
            type="button"
            onClick={() => setFilter((f) => ({ ...f, kind: "" }))}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${!filter.kind ? "bg-[#378ADD]/15 text-[#378ADD] border-[#378ADD]/40" : "text-zinc-400 border-white/10 hover:text-zinc-200"}`}
            data-testid="defense-kind-all"
          >
            Tous ({summary?.total ?? 0})
          </button>
          {Object.entries(kinds).map(([k, label]) => (
            <button
              key={k}
              type="button"
              onClick={() => setFilter((f) => ({ ...f, kind: f.kind === k ? "" : k }))}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${filter.kind === k ? "bg-[#378ADD]/15 text-[#378ADD] border-[#378ADD]/40" : "text-zinc-400 border-white/10 hover:text-zinc-200"}`}
              data-testid={`defense-kind-${k}`}
            >
              {label} ({summary?.by_kind?.[k] ?? 0})
            </button>
          ))}
        </div>
        <button type="button" onClick={refresh} disabled={refreshing} className="btn-primary text-xs disabled:opacity-50" data-testid="defense-refresh-btn">
          {refreshing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
          {refreshing ? "Scan…" : "Scanner maintenant"}
        </button>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative">
          <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-500" />
          <input
            value={filter.q}
            onChange={(e) => setFilter((f) => ({ ...f, q: e.target.value }))}
            onKeyDown={(e) => e.key === "Enter" && load()}
            placeholder="Rechercher un titre…"
            className="noam-input pl-8 text-xs h-8 w-56"
            data-testid="defense-search-input"
          />
        </div>
        <select
          value={filter.country}
          onChange={(e) => setFilter((f) => ({ ...f, country: e.target.value }))}
          className="noam-input text-xs h-8 w-44"
          data-testid="defense-country-select"
        >
          <option value="">Tous les pays</option>
          {(summary?.top_countries || []).map((c) => (
            <option key={c.country} value={c.country}>{c.country} ({c.n})</option>
          ))}
        </select>
        {summary?.last_crawl_at && (
          <span className="text-[10px] text-zinc-500 ml-auto" data-testid="defense-last-crawl">
            Dernier scan : {new Date(summary.last_crawl_at).toLocaleString("fr-CH")}
          </span>
        )}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16 text-zinc-500 text-sm">
          <Loader2 className="w-4 h-4 animate-spin mr-2" /> Chargement…
        </div>
      ) : items.length === 0 ? (
        <div className="noam-card p-8 text-center text-sm text-zinc-500" data-testid="defense-empty">
          <Shield className="w-8 h-8 mx-auto mb-2 opacity-40" />
          Aucun événement défense. Lancez un scan pour alimenter le flux.
        </div>
      ) : (
        <div className="space-y-2" data-testid="defense-events-list">
          {items.map((it) => (
            <div key={it.article_id} className="noam-card p-4 hover:border-white/20 transition-colors" data-testid="defense-event-item">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <span className="px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider bg-[#378ADD]/10 text-[#378ADD] border border-[#378ADD]/25">
                      {kinds[it.kind] || it.kind}
                    </span>
                    <span className={`px-2 py-0.5 rounded text-[10px] font-semibold border ${POLARITY_STYLE[it.sentiment_polarity] || POLARITY_STYLE.neutral}`}>
                      {it.sentiment_polarity === "negative" ? "Tension" : it.sentiment_polarity === "positive" ? "Apaisement" : "Neutre"}
                    </span>
                    {(it.countries || []).slice(0, 4).map((c) => (
                      <span key={c} className="px-1.5 py-0.5 rounded text-[10px] font-mono text-zinc-400 bg-white/5">{c}</span>
                    ))}
                  </div>
                  <div className="text-sm font-semibold text-zinc-100 leading-snug">{it.title}</div>
                  {it.summary && <p className="text-xs text-zinc-400 mt-1 leading-relaxed line-clamp-2">{it.summary}</p>}
                  {(it.entities || []).length > 0 && (
                    <div className="text-[10px] text-zinc-500 mt-1.5">
                      Entités : {it.entities.join(" · ")}
                    </div>
                  )}
                </div>
                <div className="flex flex-col items-end gap-1 flex-shrink-0">
                  <span className="text-[10px] text-zinc-500 whitespace-nowrap">
                    {it.published_at ? new Date(it.published_at).toLocaleDateString("fr-CH") : ""}
                  </span>
                  {it.url && (
                    <a href={it.url} target="_blank" rel="noreferrer" className="text-[#EF9F27] hover:underline text-[11px] inline-flex items-center gap-1" data-testid="defense-event-link">
                      Lire <ExternalLink className="w-3 h-3" />
                    </a>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
